import React from 'react';
import _omit from 'lodash/omit';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { updateCmailMessage, deleteMessages } from '../../../api/cmail.api';
import { useMeQuery } from '../../../components/hooks/useMeQuery';
import { useQueryParams } from '../../../components/providers/QueryParamsProvider';
import { queries } from '../../../queries';
import { TrustLevel } from '../../../api/cmail/cmail.dto';

export const ViewType = {
  LISTING: 'LISTING',
  DETAIL: 'DETAIL',
  COMPOSER: 'COMPOSER',
};

export const DetailViewMode = {
  SIDE_BY_SIDE: 'SIDE_BY_SIDE',
  FULL_SCREEN: 'FULL_SCREEN',
};

export enum FilterType {
  INBOX = 'INBOX',
  SENT = 'SENT',
  ARCHIVE = 'ARCHIVE',
  LOW_TRUST = 'LOW_TRUST',
  CLAIMS = 'CLAIMS',
  SUBMISSIONS = 'SUBMISSIONS',
  BOR = 'BOR',
  CRS = 'CRS',
  UNREAD = 'UNREAD',
  READ = 'READ',
  HIGH_PRIORITY = 'HIGH_PRIORITY',
  MED_PRIORITY = 'MED_PRIORITY',
  LOW_PRIORITY = 'LOW_PRIORITY',
  COMPLETE = 'COMPLETE',
  INCOMPLETE = 'INCOMPLETE',
  IN_PROGRESS = 'IN_PROGRESS',
  ESCALATED = 'ESCALATED',
  ON_HOLD = 'ON_HOLD',
  OTHER = 'OTHER',
  FAILED = 'FAILED',
  DEFAULT = 'DEFAULT',
  DRAFTS = 'DRAFTS',
  ASSIGNED_TO_ME = 'ASSIGNED_TO_ME',
  ASSIGNED_TO_USER = 'ASSIGNED_TO_USER',
}

type FilterValue = { [key: string]: { [key: string]: any } };
type FilterFunction = (userEmail: string) => FilterValue;

export const DataFilter: Record<
  FilterType | string,
  FilterValue | FilterFunction
> = {
  [FilterType.INBOX]: { isArchived: { bo: 0 } },
  [FilterType.SENT]: { isArchived: { bo: 0 } }, // Placeholder - will be handled specially
  [FilterType.ARCHIVE]: { isArchived: { bo: 1 } },
  [FilterType.LOW_TRUST]: {
    'senderTrustDecision.trustLevel.keyword': { eq: TrustLevel.LOW },
  },
  [FilterType.CLAIMS]: { 'msgCategory.keyword': { co: 'CLAIMS' } },
  [FilterType.SUBMISSIONS]: { 'msgCategory.keyword': { co: 'SUBMISSION' } },
  [FilterType.BOR]: { 'msgCategory.keyword': { co: 'BOR' } },
  [FilterType.CRS]: { 'msgCategory.keyword': { co: 'CRS' } },
  [FilterType.UNREAD]: (userEmail: string) => ({ seenBy: { dnc: userEmail } }),
  [FilterType.READ]: (userEmail: string) => ({ seenBy: { co: userEmail } }),
  [FilterType.HIGH_PRIORITY]: { priority: { gt: 6 } },
  [FilterType.MED_PRIORITY]: { priority: { gt: 4, lt: 7 } },
  [FilterType.LOW_PRIORITY]: { priority: { lt: 5 } },
  [FilterType.COMPLETE]: { 'label.keyword': { eq: 'COMPLETE' } },
  [FilterType.INCOMPLETE]: { 'label.keyword': { eq: 'INCOMPLETE' } },
  [FilterType.IN_PROGRESS]: { 'label.keyword': { eq: 'IN_PROGRESS' } },
  [FilterType.ESCALATED]: { 'label.keyword': { eq: 'ESCALATED' } },
  [FilterType.ON_HOLD]: { 'label.keyword': { eq: 'ON_HOLD' } },
  [FilterType.OTHER]: { 'label.keyword': { eq: 'OTHER' } },
  [FilterType.FAILED]: { 'label.keyword': { eq: 'FAILED' } },
  [FilterType.DEFAULT]: { 'label.keyword': { eq: 'OTHER' } },
  [FilterType.DRAFTS]: {}, // emailDirection is handled as main parameter
  [FilterType.ASSIGNED_TO_ME]: (userEmail: string) => ({
    assignedToEmail: { eq: userEmail },
  }),
  [FilterType.ASSIGNED_TO_USER]: {}, // Will be set dynamically
};

export const useCmailState = () => {
  const location = useLocation();
  const { query, replace } = useQueryParams();
  const { data: sessionUser } = useMeQuery();
  const queryClient = useQueryClient();

  const [selection, setSelection] = React.useState({});
  const [view, setView] = React.useState(ViewType.LISTING);
  const [targetMessage, setTargetMessage] = React.useState(null);
  const [detailViewMode, setDetailViewMode] = React.useState(
    DetailViewMode.FULL_SCREEN
  );

  const [filterType, setFilterType] = React.useState(FilterType.INBOX);
  const [assignedToEmail, setAssignedToEmail] = React.useState<string | null>(
    null
  );

  const filterParam = React.useMemo(() => {
    let baseFilter =
      typeof DataFilter[filterType] === 'function'
        ? (DataFilter[filterType] as FilterFunction)(sessionUser?.email)
        : (DataFilter[filterType] as FilterValue);

    // If ASSIGNED_TO_USER is active and we have an email, apply it
    if (filterType === FilterType.ASSIGNED_TO_USER && assignedToEmail) {
      baseFilter = { assignedToEmail: { eq: assignedToEmail } };
    }

    return baseFilter;
  }, [filterType, sessionUser?.email, assignedToEmail]);

  const [categoryFilter, setCategoryFilter] = React.useState({});
  const [priorityFilter, setPriorityFilter] = React.useState({});

  const showSplitScreen =
    detailViewMode === DetailViewMode.SIDE_BY_SIDE && !!targetMessage;

  React.useEffect(() => {
    // Only apply filters if we have the basic query parameters set
    // This prevents conflicts with useBackfill during initial load
    if (query.after && query.before) {
      applyFilters();
    }
    // eslint-disable-next-line
  }, [
    categoryFilter,
    priorityFilter,
    filterType,
    assignedToEmail,
    query.after,
    query.before,
  ]);

  React.useEffect(() => {
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    if (location.state?.msgId) {
      // @ts-expect-error TS(2571): Object is of type 'unknown'.
      goToDetailView({ msgId: location.state?.msgId, seenBy: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSelect = (message: any) => {
    if (message.msgId in selection) {
      setSelection((prev) => _omit(prev, message.msgId));
    } else {
      setSelection((prev) => ({ ...prev, [message.msgId]: message }));
    }
  };

  const onClearSelection = () => {
    setSelection({});
  };

  const batchUpdateMessages = (
    messages: any[] = [],
    updateData: any,
    errorMessage: string
  ) => {
    const msgIds = messages.map((message) => message.msgId);
    Promise.all(
      msgIds.map((msgId) =>
        updateCmailMessage({
          params: { msgId },
          data: updateData,
        })
      )
    )
      .then(() => {
        setTimeout(() => {
          queryClient.invalidateQueries(queries.cmail.list._def);
        }, 200);
        onClearSelection();
      })
      .catch((error) => {
        console.error(errorMessage, error);
        onClearSelection();
      });
  };

  const batchMarkAsRead = (messages: any[] = []) => {
    // For batch operations, we need to handle each message individually
    // since each message may have different seenBy arrays
    const msgIds = messages.map((message) => message.msgId);
    const userEmail = sessionUser?.email;

    if (!userEmail) {
      console.warn('Cannot mark messages as read: user email is undefined');
      return;
    }

    Promise.all(
      msgIds.map((msgId) => {
        const message = messages.find((m) => m.msgId === msgId);
        const currentSeenBy = message?.seenBy || [];
        const updatedSeenBy = currentSeenBy.includes(userEmail)
          ? currentSeenBy
          : [...currentSeenBy, userEmail];

        return updateCmailMessage({
          params: { msgId },
          data: { seenBy: updatedSeenBy },
        });
      })
    )
      .then(() => {
        setTimeout(() => {
          queryClient.invalidateQueries(queries.cmail.list._def);
        }, 200);
        onClearSelection();
      })
      .catch((error) => {
        console.error('Error marking messages as read:', error);
        onClearSelection();
      });
  };

  const batchMarkAsUnread = (messages: any[] = []) => {
    // For batch operations, we need to handle each message individually
    // since each message may have different seenBy arrays
    const msgIds = messages.map((message) => message.msgId);
    const userEmail = sessionUser?.email;

    if (!userEmail) {
      console.warn('Cannot mark messages as unread: user email is undefined');
      return;
    }

    Promise.all(
      msgIds.map((msgId) => {
        const message = messages.find((m) => m.msgId === msgId);
        const currentSeenBy = message?.seenBy || [];
        const updatedSeenBy = currentSeenBy.filter(
          (email: string) => email !== userEmail
        );

        return updateCmailMessage({
          params: { msgId },
          data: { seenBy: updatedSeenBy },
        });
      })
    )
      .then(() => {
        setTimeout(() => {
          queryClient.invalidateQueries(queries.cmail.list._def);
        }, 200);
        onClearSelection();
      })
      .catch((error) => {
        console.error('Error marking messages as unread:', error);
        onClearSelection();
      });
  };

  const batchMarkAsArchived = (messages: any[] = []) => {
    batchUpdateMessages(
      messages,
      { isArchived: true },
      'Error marking messages as archived:'
    );
  };

  const batchMarkAsUnarchived = (messages: any[] = []) => {
    batchUpdateMessages(
      messages,
      { isArchived: false },
      'Error marking messages as unarchived:'
    );
  };

  const markAsRead = (message: any) => {
    const userEmail = sessionUser?.email;

    if (!userEmail) {
      console.warn('Cannot mark message as read: user email is undefined');
      return;
    }

    const currentSeenBy = message?.seenBy || [];
    const updatedSeenBy = currentSeenBy.includes(userEmail)
      ? currentSeenBy
      : [...currentSeenBy, userEmail];

    updateCmailMessage({
      params: { msgId: message.msgId },
      data: { seenBy: updatedSeenBy },
    })
      .then(() => {
        setTimeout(() => {
          queryClient.invalidateQueries(queries.cmail.list._def);
        }, 200);
        onClearSelection();
      })
      .catch((error) => {
        console.error('Error marking message as read:', error);
        onClearSelection();
      });
  };

  const markAsUnread = (message: any) => {
    const userEmail = sessionUser?.email;

    if (!userEmail) {
      console.warn('Cannot mark message as unread: user email is undefined');
      return;
    }

    const currentSeenBy = message?.seenBy || [];
    const updatedSeenBy = currentSeenBy.filter(
      (email: string) => email !== userEmail
    );

    updateCmailMessage({
      params: { msgId: message.msgId },
      data: { seenBy: updatedSeenBy },
    })
      .then(() => {
        setTimeout(() => {
          queryClient.invalidateQueries(queries.cmail.list._def);
        }, 200);
        onClearSelection();
      })
      .catch((error) => {
        console.error('Error marking message as unread:', error);
        onClearSelection();
      });
  };

  const markAsArchived = (message: any) => {
    updateCmailMessage({
      params: { msgId: message.msgId },
      data: { isArchived: true },
    })
      .then(() => {
        setTimeout(() => {
          queryClient.invalidateQueries(queries.cmail.list._def);
        }, 200);
        onClearSelection();
      })
      .catch((error) => {
        console.error('Error marking message as archived:', error);
        onClearSelection();
      });
  };

  const markAsUnarchived = (message: any) => {
    updateCmailMessage({
      params: { msgId: message.msgId },
      data: { isArchived: false },
    })
      .then(() => {
        setTimeout(() => {
          queryClient.invalidateQueries(queries.cmail.list._def);
        }, 200);
        onClearSelection();
      })
      .catch((error) => {
        console.error('Error marking message as unarchived:', error);
        onClearSelection();
      });
  };

  const deleteMessage = (message: any) => {
    deleteMessages({
      data: [message.msgId],
    })
      .then(() => {
        // Use the correct query invalidation pattern
        queryClient.invalidateQueries(queries.cmail.list._def);
        queryClient.invalidateQueries(['draft-stats']);
        onClearSelection();
      })
      .catch((error) => {
        console.error('Error deleting message:', error);
        onClearSelection();
      });
  };

  const batchDeleteMessages = (messages: any[] = []) => {
    const msgIds = messages.map((message) => message.msgId);
    deleteMessages({
      data: msgIds,
    })
      .then(() => {
        // Use the correct query invalidation pattern
        queryClient.invalidateQueries(queries.cmail.list._def);
        queryClient.invalidateQueries(['draft-stats']);
        onClearSelection();
      })
      .catch((error) => {
        console.error('Error deleting messages:', error);
        onClearSelection();
      });
  };

  const goToListingView = () => {
    setView(ViewType.LISTING);
    setTargetMessage(null);
  };

  const goToDetailView = (message: any) => {
    // For draft emails, open in composer instead of detail view
    if (message.emailDirection === 'DRAFT') {
      goToComposerView(message);
      return;
    }

    // For sent emails (OUTGOING), open in detail view like inbox emails
    if (message.emailDirection === 'SENT') {
      // Fetch the full message details for sent emails
      queryClient
        .fetchQuery(queries.cmail.detail(message.msgId))
        .then((data) => {
          setTargetMessage(data);
          if (detailViewMode === DetailViewMode.FULL_SCREEN) {
            setView(ViewType.DETAIL);
          }
          // Mark as read after fetching details
          markAsRead(message);
        })
        .catch((error) => {
          console.error('Failed to fetch sent email details:', error);
          // Fallback to using the message data we have
          setTargetMessage(message);
          if (detailViewMode === DetailViewMode.FULL_SCREEN) {
            setView(ViewType.DETAIL);
          }
          markAsRead(message);
        });
      return;
    }

    // For inbox emails, fetch the full message details
    queryClient
      .fetchQuery(queries.cmail.detail(message.msgId))
      .then((data) => {
        setTargetMessage(data);
        if (detailViewMode === DetailViewMode.FULL_SCREEN) {
          setView(ViewType.DETAIL);
        }
        // Mark as read after fetching details
        markAsRead(message);
      })
      .catch((error) => {
        console.error('Failed to fetch inbox email details:', error);
        // Fallback to using the message data we have
        setTargetMessage(message);
        if (detailViewMode === DetailViewMode.FULL_SCREEN) {
          setView(ViewType.DETAIL);
        }
        markAsRead(message);
      });
  };

  const goToComposerView = (message: any) => {
    // For draft emails, we need to fetch the full message details
    // since we need the complete data for the composer
    queryClient
      .fetchQuery(queries.cmail.detail(message.msgId))
      .then((data) => {
        setTargetMessage(data);
        // Set a special flag to indicate this should open in composer
        setView(ViewType.COMPOSER);
      })
      .catch((error) => {
        console.error('Failed to fetch draft details:', error);
        // Fallback to using the message data we have
        setTargetMessage(message);
        setView(ViewType.COMPOSER);
      });
  };

  const applyFilters = () => {
    let filters = {};
    if (filterType === FilterType.ASSIGNED_TO_USER && assignedToEmail) {
      // Handle ASSIGNED_TO_USER with the specific email
      filters = {
        assignedToEmail: { eq: assignedToEmail },
      };
    } else if (filterType in DataFilter) {
      const filter = DataFilter[filterType];
      filters =
        typeof filter === 'function' ? filter(sessionUser?.email) : filter;
    }

    // Add category filter if present
    if (
      categoryFilter &&
      DataFilter[categoryFilter as keyof typeof DataFilter]
    ) {
      const filter = DataFilter[categoryFilter as keyof typeof DataFilter];
      filters = {
        ...filters,
        ...(typeof filter === 'function' ? filter(sessionUser?.email) : filter),
      };
    }

    // Add priority filter if present
    if (
      priorityFilter &&
      DataFilter[priorityFilter as keyof typeof DataFilter]
    ) {
      const filter = DataFilter[priorityFilter as keyof typeof DataFilter];
      filters = {
        ...filters,
        ...(typeof filter === 'function' ? filter(sessionUser?.email) : filter),
      };
    }

    const newQuery = {
      ...query,
      f: filters,
      page: 0, // Always reset to page 0 when filters change
    };

    // Always reset page to 0 when applying filters to ensure we start from the first page
    // This prevents issues where switching between different filter types (INBOX, SENT, DRAFTS)
    // might not be detected as a filter change but should still reset pagination
    replace(location.pathname, { query: newQuery });
  };

  const onFilterChange = (
    filterName: FilterType,
    bucket?: string,
    customEmail?: string
  ) => {
    if (!bucket) {
      // Always switch to listing view when changing filters
      setView(ViewType.LISTING);
      setTargetMessage(null);

      // Handle ASSIGNED_TO_USER with custom email
      if (filterName === FilterType.ASSIGNED_TO_USER && customEmail) {
        setAssignedToEmail(customEmail);
        setFilterType(filterName);
        return;
      }

      // Toggle behavior for UNREAD filter
      if (filterName === FilterType.UNREAD) {
        setFilterType(
          filterType === FilterType.UNREAD
            ? FilterType.INBOX
            : FilterType.UNREAD
        );
      } else if (filterType === filterName) {
        // If clicking the same filter, switch back to INBOX
        setFilterType(FilterType.INBOX);
        setAssignedToEmail(null); // Clear assigned email when switching away
      } else {
        // Set the new filter type
        setFilterType(filterName);
        // Clear assigned email for non-ASSIGNED_TO_USER filters
        if (filterName !== FilterType.ASSIGNED_TO_USER) {
          setAssignedToEmail(null);
        }
      }
    }
    if (bucket === 'CATEGORY') {
      if (categoryFilter === filterName) {
        setCategoryFilter({});
      } else {
        setCategoryFilter(filterName);
      }
    }
    if (bucket === 'PRIORITY') {
      if (priorityFilter === filterName) {
        setPriorityFilter({});
      } else {
        setPriorityFilter(filterName);
      }
    }
    if (bucket === 'LABEL') {
      if (filterType === filterName) {
        setFilterType(FilterType.INBOX);
      } else {
        setFilterType(filterName);
      }
    }
  };

  const onDetailViewModeChange = (mode: any) => {
    setDetailViewMode(mode);
  };

  return {
    selection,
    onSelect,
    onClearSelection,
    view,
    goToListingView,
    goToDetailView,
    targetMessage,
    markAsRead,
    batchMarkAsRead,
    markAsUnread,
    markAsArchived,
    batchMarkAsArchived,
    markAsUnarchived,
    batchMarkAsUnarchived,
    batchMarkAsUnread,
    deleteMessage,
    batchDeleteMessages,
    filterType,
    filterParam,
    onFilterChange,
    categoryFilter,
    priorityFilter,
    assignedToEmail,
    detailViewMode,
    showSplitScreen,
    onDetailViewModeChange,
    goToComposerView,
  };
};
